/**
 * Pre-auth and self-service auth endpoints under `/api/auth/`.
 *
 * Kept apart from `endpoints.ts`, which belongs to the rest of the product, so the
 * sign-in, Telegram and password flows change without touching that file.
 */
import { apiRequest } from "@/lib/api";

/** Public values the sign-in page needs before anyone is signed in. */
export interface AuthConfig {
  /** The bot's @username for the widget. Empty when Telegram is not configured. */
  telegram_bot_username: string;
  /** Empty until Google sign-in exists (a later phase). */
  google_client_id: string;
}

/**
 * The payload the Telegram Login widget hands to its callback, passed back unchanged:
 * the server checks a signature over every field except `hash`.
 */
export interface TelegramWidgetUser {
  id: number;
  auth_date: number;
  hash: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
}

/** The connected Telegram account, or `connected: false` when there is none. */
export interface TelegramIdentity {
  connected: boolean;
  id?: number;
  provider?: string;
  username?: string;
  display_name?: string;
  last_used_at?: string | null;
  created_at?: string;
}

export interface AccessTokenResponse {
  /** The refresh token travels as an HttpOnly cookie, never in the body. */
  access: string;
}

export interface DetailResponse {
  detail: string;
}

export interface PasswordChangeBody {
  current_password: string;
  new_password: string;
}

export interface PasswordResetBody {
  uid: string;
  token: string;
  password: string;
}

export const authApi = {
  config: (): Promise<AuthConfig> =>
    apiRequest<AuthConfig>("/api/auth/config/", { anonymous: true }),

  telegramSignIn: (payload: TelegramWidgetUser): Promise<AccessTokenResponse> =>
    apiRequest<AccessTokenResponse>("/api/auth/telegram/", {
      method: "POST",
      body: payload,
      anonymous: true,
    }),

  telegramIdentity: (): Promise<TelegramIdentity> =>
    apiRequest<TelegramIdentity>("/api/auth/telegram/identity/"),

  connectTelegram: (payload: TelegramWidgetUser): Promise<TelegramIdentity> =>
    apiRequest<TelegramIdentity>("/api/auth/telegram/identity/", {
      method: "POST",
      body: payload,
    }),

  disconnectTelegram: (): Promise<null> =>
    apiRequest<null>("/api/auth/telegram/identity/", { method: "DELETE" }),

  forgotPassword: (email: string): Promise<DetailResponse> =>
    apiRequest<DetailResponse>("/api/auth/password/forgot/", {
      method: "POST",
      body: { email },
      anonymous: true,
    }),

  resetPassword: (body: PasswordResetBody): Promise<DetailResponse> =>
    apiRequest<DetailResponse>("/api/auth/password/reset/", {
      method: "POST",
      body,
      anonymous: true,
    }),

  changePassword: (body: PasswordChangeBody): Promise<DetailResponse> =>
    apiRequest<DetailResponse>("/api/auth/password/change/", { method: "POST", body }),
};
